import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Users, BarChart3, Clock, CheckCircle, PauseCircle, Activity, FileText, UploadCloud, Briefcase, ClipboardList } from 'lucide-react';
import { API_BASE_URL } from '../api/config';

const DashboardPage = () => {
  const [stats, setStats] = useState({
    overall: { wip_login: 0, disbursed: 0, hold: 0, pending: 0 },
    daily: { wip_login: 0, disbursed: 0, hold: 0, pending: 0 },
    weekly: { wip_login: 0, disbursed: 0, hold: 0, pending: 0 },
    monthly: { wip_login: 0, disbursed: 0, hold: 0, pending: 0 },
    forms_added: 0,
    uploaded: 0,
    total_employees: 0,
    total_tasks: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/dashboard_stats.php`);
        const json = await response.json();
        if (json.success) {
          // If the backend doesn't have the new format yet, handle gracefully
          const d = json.data;
          setStats({
            overall: d.overall || { wip_login: d.wip_login||0, disbursed: d.disbursed||0, hold: d.hold||0, pending: d.pending||0 },
            daily: d.daily || { wip_login: 0, disbursed: 0, hold: 0, pending: 0 },
            weekly: d.weekly || { wip_login: 0, disbursed: 0, hold: 0, pending: 0 },
            monthly: d.monthly || { wip_login: 0, disbursed: 0, hold: 0, pending: 0 },
            forms_added: d.forms_added || 0,
            uploaded: d.uploaded || 0,
            total_employees: d.total_employees || 0,
            total_tasks: d.total_tasks || 0
          });
        }
      } catch (err) {
        console.error('Failed to fetch dashboard stats', err);
      }
    };
    fetchStats();
  }, []);

  const renderStatusRow = (title, statusData, subtitle) => (
    <>
      <h2 className="text-xl font-bold text-gray-800 mb-4">{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        
        {/* WIP-Login */}
        <div className="relative group">
          <div className="absolute inset-0 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-300" style={{background: 'linear-gradient(to right, #3b82f6, #2563eb)'}}></div>
          <div className="relative rounded-2xl p-6 text-white shadow-lg overflow-hidden bg-gradient-to-br from-blue-500 to-blue-700">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-20 h-20 rounded-full opacity-20 bg-blue-300"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-8">
                <div className="text-sm font-medium text-blue-100">WIP-Login</div>
                <Activity size={24} className="text-blue-200" />
              </div>
              <div className="mb-2">
                <p className="text-4xl font-bold">{statusData?.wip_login || 0}</p>
                <p className="text-blue-100 text-xs mt-1">{subtitle}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Disbursed */}
        <div className="relative group">
          <div className="absolute inset-0 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-300 bg-gradient-to-r from-emerald-500 to-emerald-600"></div>
          <div className="relative rounded-2xl p-6 text-white shadow-lg overflow-hidden bg-gradient-to-br from-emerald-500 to-emerald-700">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-20 h-20 rounded-full opacity-20 bg-emerald-400"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-8">
                <div className="text-sm font-medium text-emerald-100">Disbursed</div>
                <CheckCircle size={24} className="text-emerald-200" />
              </div>
              <div className="mb-2">
                <p className="text-4xl font-bold">{statusData?.disbursed || 0}</p>
                <p className="text-emerald-100 text-xs mt-1">{subtitle}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Hold */}
        <div className="relative group">
          <div className="absolute inset-0 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-300" style={{background: 'linear-gradient(to right, #f59e0b, #d97706)'}}></div>
          <div className="relative rounded-2xl p-6 text-white shadow-lg overflow-hidden bg-gradient-to-br from-yellow-500 to-yellow-600">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-20 h-20 rounded-full opacity-20 bg-yellow-300"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-8">
                <div className="text-sm font-medium text-yellow-100">Hold</div>
                <PauseCircle size={24} className="text-yellow-200" />
              </div>
              <div className="mb-2">
                <p className="text-4xl font-bold">{statusData?.hold || 0}</p>
                <p className="text-yellow-100 text-xs mt-1">{subtitle}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Pending */}
        <div className="relative group">
          <div className="absolute inset-0 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-300" style={{background: 'linear-gradient(to right, #f97316, #ea580c)'}}></div>
          <div className="relative rounded-2xl p-6 text-white shadow-lg overflow-hidden bg-gradient-to-br from-orange-500 to-orange-600">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-20 h-20 rounded-full opacity-20 bg-orange-300"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-8">
                <div className="text-sm font-medium text-orange-100">Pending</div>
                <Clock size={24} className="text-orange-200" />
              </div>
              <div className="mb-2">
                <p className="text-4xl font-bold">{statusData?.pending || 0}</p>
                <p className="text-orange-100 text-xs mt-1">{subtitle}</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );

  return (
    <div className="dashboard-light-theme pb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 text-sm mt-1">Welcome back! Here's your operations overview</p>
        </div>
      </div>

      {renderStatusRow("Daily Application Statuses", stats.daily, "Daily count")}
      {renderStatusRow("Weekly Application Statuses", stats.weekly, "Weekly count")}
      {renderStatusRow("Monthly Application Statuses", stats.monthly, "Monthly count")}
      {renderStatusRow("Overall Application Statuses", stats.overall, "Total count")}

      {/* Row 2: Operational Stats */}
      <h2 className="text-xl font-bold text-gray-800 mb-4">Operations Summary</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">

        {/* Total Forms Added */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex items-center gap-5 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
            <FileText size={24} />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Total Forms Added</p>
            <p className="text-2xl font-bold text-gray-900">{stats.forms_added}</p>
          </div>
        </div>

        {/* Total Uploaded */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex items-center gap-5 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center text-purple-600">
            <UploadCloud size={24} />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Total Uploaded</p>
            <p className="text-2xl font-bold text-gray-900">{stats.uploaded}</p>
          </div>
        </div>

        {/* Total Employee */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex items-center gap-5 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600">
            <Briefcase size={24} />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Total Employees</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total_employees}</p>
          </div>
        </div>

        {/* Total Task DA */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex items-center gap-5 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600">
            <ClipboardList size={24} />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Total Tasks</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total_tasks}</p>
          </div>
        </div>

      </div>
      
      {/* Extra space at the bottom */}
      <div className="h-16"></div>
    </div>
  );
};

export default DashboardPage;
